/**
 * Training Service
 * API calls for training plan management
 */

import api from './api';
import { STORAGE_KEYS } from '../utils/training/constants';
import { sendOrQueue } from '../utils/offlineQueue';
import { getToken } from '../utils/tokenStorage';

/** api.ts prefixes nothing (same-origin deployment) — keep the same base. */
const API_URL = '/api';

// Training plan interface
export interface TrainingPlan {
  id?: string;
  plan_id?: string;
  name?: string;
  workoutDuration?: number;
  trainingWeeks?: number;
  hasDeloadWeek?: boolean;
  deloadWeek?: number;
  trainingDays?: string[];
  hiitWalkingDays?: string[];
  weightUnit?: string;
  bodyweight?: string;
  bodyMeasurements?: Record<string, unknown>;
  measurementUnit?: string;
  selectedMuscles?: string[];
  dayMuscleAssignments?: Record<string, string[]>;
  dayExercises?: Record<string, unknown[]>;
  deloadExercises?: Record<string, unknown[]>;
  currentWeek?: number;
  currentDayIndex?: number;
  createdAt?: string;
}

// API response interfaces
interface PlanResponse {
  data?: TrainingPlan;
  error?: string;
  savedLocally?: boolean;
}

interface PlansResponse {
  data?: TrainingPlan[];
}

interface ProgressResponse {
  data?: unknown;
  error?: string;
}

interface DeleteResponse {
  success?: boolean;
  error?: string;
}

/**
 * Get the active training plan
 */
export async function getActivePlan(): Promise<TrainingPlan | null> {
  try {
    // The endpoint answers with the plan FLAT; older callers expected a
    // `data` wrapper, which silently returned null for everyone (iter358).
    const response: any = await api.get<any>('/plans/active');
    if (response?.data) return response.data;
    return response?.dayExercises ? (response as TrainingPlan) : null;
  } catch (error) {
    console.error('[TrainingService] Failed to get active plan:', error);
    return null;
  }
}

/**
 * iter357 — make the first offline open of Workout possible.
 *
 * The plan was only cached once the user had opened Workout ONLINE in that
 * session, so a fresh login followed by a signal drop showed "No Active
 * Workout". Called right after login; never overwrites a local plan.
 */
export async function prefetchActivePlan(): Promise<void> {
  try {
    if (localStorage.getItem(STORAGE_KEYS.ACTIVE_PLAN)) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    // iter358 — /api/plans/active returns the plan FLAT, so the old
    // `response.data` read was always null and nothing was ever cached.
    const res: any = await api.get<any>('/plans/active');
    const plan = res?.data?.dayExercises ? res.data : (res?.dayExercises ? res : null);
    if (plan && !localStorage.getItem(STORAGE_KEYS.ACTIVE_PLAN)) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PLAN, JSON.stringify(plan));
    }
  } catch { /* offline — nothing to cache */ }
}

/**
 * Save/update the active training plan
 */
export async function saveActivePlan(plan: TrainingPlan): Promise<PlanResponse> {
  // Always keep the local copy first — it is the offline source of truth.
  localStorage.setItem(STORAGE_KEYS.ACTIVE_PLAN, JSON.stringify(plan));
  try {
    // iter355 — this PUT used to bypass the offline queue, so a workout
    // finished with no signal left `plans/active` stuck on the old day while
    // the queued `training-plan` write moved on. The two stores disagreed and
    // another device could show the finished day again.
    const res = await sendOrQueue(`${API_URL}/plans/active`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
      body: JSON.stringify(plan),
      credentials: 'include' as RequestCredentials,
    }, { label: 'Training plan', dedupeKey: 'PUT:/api/plans/active' });
    if (!res) return { savedLocally: true };
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    return { data: data.data ?? data };
  } catch (error) {
    const err = error as Error;
    console.error('[TrainingService] Failed to save active plan:', err);
    return { error: err.message, savedLocally: true };
  }
}

/**
 * Update plan progress (week, day, exercises)
 */
export async function updatePlanProgress(planId: string, progressData: Partial<TrainingPlan>): Promise<ProgressResponse> {
  try {
    const response = await api.request<ProgressResponse>(`/api/plans/${planId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify(progressData),
    });
    return { data: response.data };
  } catch (error) {
    const err = error as Error;
    console.error('[TrainingService] Failed to update plan progress:', err);
    return { error: err.message };
  }
}

/**
 * Get all training plans for the user
 */
export async function getUserPlans(): Promise<TrainingPlan[]> {
  try {
    const response = await api.get<PlansResponse>('/plans');
    return response.data || [];
  } catch (error) {
    console.error('[TrainingService] Failed to get user plans:', error);
    return [];
  }
}

/**
 * Create a new training plan
 */
export async function createPlan(planData: TrainingPlan): Promise<PlanResponse> {
  try {
    const response = await api.post<PlanResponse>('/plans', planData);
    return { data: response.data };
  } catch (error) {
    const err = error as Error;
    console.error('[TrainingService] Failed to create plan:', err);
    return { error: err.message };
  }
}

/**
 * Delete a training plan
 */
export async function deletePlan(planId: string): Promise<DeleteResponse> {
  try {
    await api.delete(`/plans/${planId}`);
    return { success: true };
  } catch (error) {
    const err = error as Error;
    console.error('[TrainingService] Failed to delete plan:', err);
    return { error: err.message };
  }
}

/**
 * Duplicate a training plan
 */
export async function duplicatePlan(planId: string, newName: string): Promise<PlanResponse> {
  try {
    const response = await api.post<PlanResponse>(`/plans/${planId}/duplicate`, { name: newName });
    return { data: response.data };
  } catch (error) {
    const err = error as Error;
    console.error('[TrainingService] Failed to duplicate plan:', err);
    return { error: err.message };
  }
}

/**
 * Get plan from localStorage (offline fallback)
 */
export function getLocalPlan(): TrainingPlan | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.ACTIVE_PLAN);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('[TrainingService] Failed to get local plan:', error);
    return null;
  }
}

/**
 * Save plan to localStorage
 */
export function saveLocalPlan(plan: TrainingPlan): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_PLAN, JSON.stringify(plan));
    return true;
  } catch (error) {
    console.error('[TrainingService] Failed to save local plan:', error);
    return false;
  }
}

export default {
  getActivePlan,
  saveActivePlan,
  updatePlanProgress,
  getUserPlans,
  createPlan,
  deletePlan,
  duplicatePlan,
  getLocalPlan,
  saveLocalPlan
};
